import { useI18n } from '@/contexts/i18n-hook';

export interface MaquinaFormData {
  equipamento: string;
  tipo: string;
  area: string;
  local: string;
  tag: string;
  item: string;
  idItem: string;
  status: string;
  funcionario: string;
  observacao: string;
  refSap: string;
  refSismanaAntigo: string;
  refSismanaNovo: string;
  numeroAmt: string;
  numeroAprPraxis: string;
  template: string;
}

/** `valor` é o que vai para a API; `chave` só aponta para o rótulo traduzido. */
const STATUS_OPTIONS = [
  { valor: 'LEVANTAR', chave: 'levantar' },
  { valor: 'LEVANTADO', chave: 'levantado' },
  { valor: 'EM ANÁLISE', chave: 'emAnalise' },
  { valor: 'APROVADO', chave: 'aprovado' },
  { valor: 'ARQUIVADO', chave: 'arquivado' },
];

interface MaquinaFormProps {
  dados: MaquinaFormData;
  onChange: (campo: keyof MaquinaFormData, valor: string) => void;
  errors?: Record<string, string>;
}

export function MaquinaForm({ dados, onChange, errors = {} }: MaquinaFormProps) {
  const { t } = useI18n();

  const inputClass =
    'block w-full px-3 py-2.5 text-sm border border-rg-line dark:border-schmersal-gray-dark rounded-lg ' +
    'focus:outline-none focus:ring-2 focus:ring-schmersal-blue focus:border-schmersal-blue ' +
    'bg-white dark:bg-schmersal-gray-darkest text-schmersal-gray-dark dark:text-white ' +
    'placeholder-schmersal-gray-medium dark:placeholder-schmersal-gray-medium transition-all duration-200';

  const errorInputClass =
    'block w-full px-3 py-2.5 text-sm border-2 border-red-500 rounded-lg ' +
    'focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 ' +
    'bg-white dark:bg-schmersal-gray-darkest text-schmersal-gray-dark dark:text-white ' +
    'placeholder-schmersal-gray-medium dark:placeholder-schmersal-gray-medium transition-all duration-200';

  const labelClass = 'block text-sm font-semibold text-schmersal-gray-dark dark:text-white mb-1.5';

  return (
    <div className="space-y-8">
      {/* Seção 1 - Identificação */}
      <div className="bg-white dark:bg-schmersal-gray-darker p-6 rounded-lg shadow-sm border border-rg-line dark:border-schmersal-gray-dark">
        <h3 className="text-lg font-bold text-schmersal-blue dark:text-white mb-5 pb-2 border-b-2 border-schmersal-gray-light dark:border-schmersal-gray-dark">
          {t('maquinas.form.secao.identificacao')}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {/* Equipamento */}
          <div className="md:col-span-2">
            <label className={labelClass}>
              {t('maquinas.form.campo.equipamento')} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={dados.equipamento}
              onChange={(e) => onChange('equipamento', e.target.value)}
              placeholder={t('maquinas.form.campo.equipamentoPlaceholder')}
              className={errors.equipamento ? errorInputClass : inputClass}
            />
            {errors.equipamento && (
              <p className="mt-1 text-xs text-red-500">{errors.equipamento}</p>
            )}
          </div>

          {/* Tipo */}
          <div>
            <label className={labelClass}>{t('comum.rotulo.tipo')}</label>
            <input
              type="text"
              value={dados.tipo}
              onChange={(e) => onChange('tipo', e.target.value)}
              placeholder={t('maquinas.form.campo.tipoPlaceholder')}
              className={inputClass}
            />
          </div>

          {/* TAG */}
          <div>
            <label className={labelClass}>{t('maquinas.form.campo.tag')}</label>
            <input
              type="text"
              value={dados.tag}
              onChange={(e) => onChange('tag', e.target.value)}
              placeholder={t('maquinas.form.campo.tagPlaceholder')}
              className={inputClass}
            />
          </div>

          {/* Item */}
          <div>
            <label className={labelClass}>{t('maquinas.form.campo.item')}</label>
            <input
              type="text"
              value={dados.item}
              onChange={(e) => onChange('item', e.target.value)}
              placeholder={t('maquinas.form.campo.itemPlaceholder')}
              className={inputClass}
            />
          </div>

          {/* ID do Item */}
          <div>
            <label className={labelClass}>{t('maquinas.form.campo.idItem')}</label>
            <input
              type="text"
              value={dados.idItem}
              onChange={(e) => onChange('idItem', e.target.value)}
              placeholder={t('maquinas.form.campo.idItemPlaceholder')}
              className={inputClass}
            />
          </div>

          {/* Status */}
          <div>
            <label className={labelClass}>{t('comum.rotulo.status')}</label>
            <select
              value={dados.status}
              onChange={(e) => onChange('status', e.target.value)}
              className={inputClass}
            >
              <option value="">{t('maquinas.form.campo.statusPlaceholder')}</option>
              {STATUS_OPTIONS.map((s) => (
                <option key={s.valor} value={s.valor}>{t(`maquinas.form.status.${s.chave}`)}</option>
              ))}
            </select>
          </div>

          {/* Responsável */}
          <div>
            <label className={labelClass}>{t('maquinas.form.campo.funcionario')}</label>
            <input
              type="text"
              value={dados.funcionario}
              onChange={(e) => onChange('funcionario', e.target.value)}
              placeholder={t('maquinas.form.campo.funcionarioPlaceholder')}
              className={inputClass}
            />
          </div>

          {/* Template */}
          <div>
            <label className={labelClass}>{t('maquinas.form.campo.template')}</label>
            <input
              type="text"
              value={dados.template}
              onChange={(e) => onChange('template', e.target.value)}
              placeholder={t('maquinas.form.campo.templatePlaceholder')}
              className={inputClass}
            />
          </div>
        </div>
      </div>

      {/* Seção 2 - Localização */}
      <div className="bg-white dark:bg-schmersal-gray-darker p-6 rounded-lg shadow-sm border border-rg-line dark:border-schmersal-gray-dark">
        <h3 className="text-lg font-bold text-schmersal-blue dark:text-white mb-5 pb-2 border-b-2 border-schmersal-gray-light dark:border-schmersal-gray-dark">
          {t('maquinas.form.secao.localizacao')}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <label className={labelClass}>{t('comum.rotulo.area')}</label>
            <input
              type="text"
              value={dados.area}
              onChange={(e) => onChange('area', e.target.value)}
              placeholder={t('maquinas.form.campo.areaPlaceholder')}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>{t('comum.rotulo.local')}</label>
            <input
              type="text"
              value={dados.local}
              onChange={(e) => onChange('local', e.target.value)}
              placeholder={t('maquinas.form.campo.localPlaceholder')}
              className={inputClass}
            />
          </div>
        </div>
      </div>

      {/* Seção 3 - Referências */}
      <div className="bg-white dark:bg-schmersal-gray-darker p-6 rounded-lg shadow-sm border border-rg-line dark:border-schmersal-gray-dark">
        <h3 className="text-lg font-bold text-schmersal-blue dark:text-white mb-5 pb-2 border-b-2 border-schmersal-gray-light dark:border-schmersal-gray-dark">
          {t('maquinas.form.secao.referencias')}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <label className={labelClass}>{t('maquinas.form.campo.numeroAprPraxis')}</label>
            <input
              type="text"
              value={dados.numeroAprPraxis}
              onChange={(e) => onChange('numeroAprPraxis', e.target.value)}
              placeholder={t('maquinas.form.campo.numeroAprPraxisPlaceholder')}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>{t('maquinas.form.campo.numeroAmt')}</label>
            <input
              type="text"
              value={dados.numeroAmt}
              onChange={(e) => onChange('numeroAmt', e.target.value)}
              placeholder={t('maquinas.form.campo.numeroAmtPlaceholder')}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>{t('maquinas.form.campo.refSap')}</label>
            <input
              type="text"
              value={dados.refSap}
              onChange={(e) => onChange('refSap', e.target.value)}
              placeholder={t('maquinas.form.campo.refSapPlaceholder')}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>{t('maquinas.form.campo.refSismanaAntigo')}</label>
            <input
              type="text"
              value={dados.refSismanaAntigo}
              onChange={(e) => onChange('refSismanaAntigo', e.target.value)}
              placeholder={t('maquinas.form.campo.refSismanaAntigoPlaceholder')}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>{t('maquinas.form.campo.refSismanaNovo')}</label>
            <input
              type="text"
              value={dados.refSismanaNovo}
              onChange={(e) => onChange('refSismanaNovo', e.target.value)}
              placeholder={t('maquinas.form.campo.refSismanaNovoPlaceholder')}
              className={inputClass}
            />
          </div>
        </div>
      </div>

      {/* Seção 4 - Observações */}
      <div className="bg-white dark:bg-schmersal-gray-darker p-6 rounded-lg shadow-sm border border-rg-line dark:border-schmersal-gray-dark">
        <h3 className="text-lg font-bold text-schmersal-blue dark:text-white mb-5 pb-2 border-b-2 border-schmersal-gray-light dark:border-schmersal-gray-dark">
          {t('maquinas.form.secao.observacoes')}
        </h3>
        <div>
          <label className={labelClass}>{t('comum.rotulo.observacoes')}</label>
          <textarea
            value={dados.observacao}
            onChange={(e) => onChange('observacao', e.target.value)}
            rows={4}
            placeholder={t('maquinas.form.campo.observacaoPlaceholder')}
            className={`${inputClass} resize-none`}
          />
        </div>
      </div>
    </div>
  );
}
